/**
 * Evaluation Hook for InboundX Chat
 *
 * This hook manages the message evaluation system for collecting user feedback
 * on chat responses. It handles the complete evaluation workflow including:
 * - Managing evaluation popup state and data
 * - Processing thumbs up/down ratings from users
 * - Handling evaluation popup interactions and callbacks
 * - Submitting evaluation data to the backend service
 * - Updating message state to reflect evaluation status
 * - Preventing duplicate evaluations on the same message
 *
 * The hook integrates with the EvaluationService to provide a seamless
 * feedback collection experience while maintaining proper state management.
 */

import { type MutableRefObject, useCallback, useState } from 'react';
import type { EvaluationPopupResult } from '../../components/EvaluationPopup';
import type { ChatMessage, EvaluationRating } from '../../services/chatStorage';
import type {
    EvaluationMessageContext,
    EvaluationService,
    EvaluationSubmissionOverride,
} from '../../services/evaluation';
import { logger } from '../../utils/core/logger';

type EvaluationSubmission = Parameters<EvaluationService['submitEvaluation']>[0];

interface UseEvaluationProps {
    evaluationService: EvaluationService | null;
    evaluationEnabled: boolean;
    currentSessionId: string;
    domain: string;
    evaluationPopupResolveRef: MutableRefObject<((result: EvaluationPopupResult) => void) | null>;
    submitEvaluationOverride?: EvaluationSubmissionOverride;
}

export const useEvaluation = ({
    evaluationService,
    evaluationEnabled,
    currentSessionId,
    domain: _domain,
    evaluationPopupResolveRef,
    submitEvaluationOverride,
}: UseEvaluationProps) => {
    const [showEvaluationPopup, setShowEvaluationPopup] = useState(false);
    const [currentEvaluationData, setCurrentEvaluationData] = useState<{
        messageId: string;
        messageContent: string;
        rating: 'thumbs_up' | 'thumbs_down';
    } | null>(null);

    const handleEvaluation = useCallback(
        async (
            messageId: string,
            messageContent: string,
            rating: 'thumbs_up' | 'thumbs_down',
            messageContext?: EvaluationMessageContext
        ) => {
            logger.debug(
                'InboundXChat: handleEvaluation called with rating:',
                rating,
                'messageId:',
                messageId
            );

            if (!evaluationService || !evaluationEnabled) {
                logger.error('InboundXChat: Evaluation service not available or disabled', {
                    evaluationService: !!evaluationService,
                    evaluationEnabled,
                });
                return { success: false };
            }

            // Check if message was already evaluated
            if (evaluationService.isMessageEvaluated(messageId)) {
                logger.error('InboundXChat: Message already evaluated');
                return { success: false };
            }

            try {
                const evaluationResult = await evaluationService.showEvaluationPopup(
                    messageContent,
                    messageId,
                    rating
                );

                if (!evaluationResult.cancelled) {
                    // Submit evaluation
                    const evaluationData: EvaluationSubmission = {
                        rating: evaluationResult.finalRating,
                        messageText: messageContent.substring(0, 200),
                        messageId,
                        url: window.location.href,
                        domain: window.location.hostname,
                        pageTitle: document.title || '',
                        timestamp: new Date().toISOString(),
                        sessionId: currentSessionId,
                        threadId: undefined,
                    };

                    // Only include comment if it exists (for thumbs down cases)
                    if (evaluationResult.comment?.trim()) {
                        evaluationData.comment = evaluationResult.comment;
                    }

                    const success = submitEvaluationOverride
                        ? await submitEvaluationOverride({
                              result: evaluationResult,
                              messageId,
                              messageContent,
                              sessionId: messageContext?.sessionId ?? currentSessionId,
                              threadId: messageContext?.threadId,
                              userMessageContent: messageContext?.userMessageContent,
                          })
                        : await evaluationService.submitEvaluation(evaluationData);

                    if (success) {
                        // Mark as evaluated
                        evaluationService.saveEvaluationState(messageId);
                        logger.debug(
                            'InboundXChat: Evaluation submitted successfully with rating:',
                            evaluationResult.finalRating
                        );
                        return {
                            success: true,
                            rating: evaluationResult.finalRating as EvaluationRating,
                        };
                    } else {
                        logger.error('InboundXChat: Failed to submit evaluation');
                        return { success: false };
                    }
                }
            } catch (error) {
                logger.error('InboundXChat: Error handling evaluation:', error);
                return { success: false };
            }

            return { success: false };
        },
        [evaluationService, evaluationEnabled, currentSessionId, submitEvaluationOverride]
    );

    const handleEvaluationPopupClose = useCallback(
        (result: EvaluationPopupResult) => {
            setShowEvaluationPopup(false);
            setCurrentEvaluationData(null);

            // Call the stored resolve function via ref
            if (evaluationPopupResolveRef.current) {
                evaluationPopupResolveRef.current(result);
                evaluationPopupResolveRef.current = null;
            }
        },
        [evaluationPopupResolveRef]
    );

    const updateMessageEvaluation = useCallback(
        (
            messages: ChatMessage[],
            messageId: string,
            evaluationRating: EvaluationRating
        ): ChatMessage[] => {
            return messages.map((msg) =>
                msg.id === messageId ? { ...msg, isEvaluated: true, evaluationRating } : msg
            );
        },
        []
    );

    return {
        showEvaluationPopup,
        currentEvaluationData,
        setShowEvaluationPopup,
        setCurrentEvaluationData,
        handleEvaluation,
        handleEvaluationPopupClose,
        updateMessageEvaluation,
        evaluationPopupResolveRef,
    };
};
